import { Button, Image, TabGroup, TabPanel } from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";
import { createFileRoute, useLoaderData, useNavigate } from "@tanstack/react-router";
import { Award, PenLine, Plus, Star } from "lucide-react";
import { useState } from "react";

import { Events, RawLayout } from "@/components";
import { orgApiClient } from "@/libs/api/org-client";

import { OrgAbout } from "../-components/org-about";
import { OrgMember } from "../-components/org-member";
import { OrgReferral } from "../-components/org-referral";

export const Route = createFileRoute("/org/$orgId/")({
  component: RouteComponent,
  loader: async ({ params }) => {
    const org = await orgApiClient.getFullDetail(params.orgId);
    return { data: { org } };
  }
});

function RouteComponent() {
  const { formatDate } = useI18n();
  const { data } = useLoaderData({ from: "/org/$orgId/" });
  const { org } = data;
  const [activeTab, setActiveTab] = useState("about");
  const navigate = useNavigate();
  const handleCreateEvent = () => {
    if (!org?.id) return;
    navigate({ to: "/org/$orgId/event/create", params: { orgId: org?.id } });
  };
  const onEditBtnClick = () => {
    if (!org?.id) return;
    navigate({ to: "/org/$orgId/edit", params: { orgId: org?.id } });
  };
  if (!org) {
    return <div>Org not found</div>;
  }
  return (
    <RawLayout>
      <div className="min-h-screen bg-background">
        {/* Hero Banner */}
        <div className="relative h-64 sm:h-80 lg:h-96 bg-background-secondary">
          <Image src={org.cover} alt={org.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-background-primary via-background-primary/80 via-20% to-transparent to-60%" />
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          {/* Company Header */}
          <div className="relative -mt-20 sm:-mt-20 mb-8 flex flex-col sm:flex-row items-start gap-6">
            {/* Logo */}
            <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-xl overflow-hidden bg-card border-[3px] border-border-light shadow-lg">
              <Image src={org.avatar} alt={org.name} className="w-full h-full object-cover" />
            </div>

            {/* Info */}
            <div className="flex-1 space-y-3 flex flex-col sm:flex-row justify-between">
              <div className="space-y-3">
                <div className="space-y-1">
                  <div className="flex flex-wrap items-start gap-2">
                    <p className="text-responsive-5xl font-normal text-text-primary">{org.name}</p>
                    {org.verified && (
                      <div className="flex items-center gap-1 text-text-primary">
                        <Award className="h-4 w-4" />
                      </div>
                    )}
                  </div>
                  <p className="text-responsive-base text-text-primary font-normal">{org.tagline}</p>
                </div>

                <div className="flex items-center gap-1">
                  <Star className="h-5 w-5 fill-amber-400 text-amber-400" />
                  <span className="font-medium">{org.stats.rating}</span>
                  <span className="text-muted-foreground">({org.stats.totalReviews} reviews)</span>
                </div>
                <p className="text-responsive-sm text-text-secondary font-medium">
                  Created {formatDate(org.createdAt, { month: "short", day: "numeric", year: "numeric" })}
                </p>
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={onEditBtnClick} variant="outline">
                  <PenLine className="h-4 w-4 mr-2" />
                  Chỉnh sửa
                </Button>
                <Button onClick={handleCreateEvent} variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Tạo sự kiện
                </Button>
              </div>
            </div>
          </div>
          <TabGroup
            items={[
              { id: "about", label: "Overview" },

              { id: "events", label: "Events" },
              { id: "referrals", label: "Wallet" },
              { id: "members", label: "Members" }
              // { id: "reviews", label: "Reviews" },
              // { id: "settings", label: "Settings" }
            ]}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            variant="underline"
          >
            <TabPanel isActive={activeTab === "about"}>
              <OrgAbout org={org} />
            </TabPanel>
            <TabPanel isActive={activeTab === "events"}>
              <Events hostId={org.id} limit={12} />
            </TabPanel>
            <TabPanel isActive={activeTab === "referrals"}>
              <OrgReferral org={org} />
            </TabPanel>
            <TabPanel isActive={activeTab === "members"}>
              <OrgMember org={org} />
            </TabPanel>
            <TabPanel isActive={activeTab === "settings"}>
              <div className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-responsive-base font-normal text-text-primary">Settings</h3>
                </div>
              </div>
            </TabPanel>
          </TabGroup>
        </div>
      </div>
    </RawLayout>
  );
}
